import classNames from 'classnames';
import {
  FC,
  useCallback,
  useState,
  useRef,
  PropsWithChildren,
  DetailedHTMLProps,
  InputHTMLAttributes,
  FocusEvent,
  ChangeEvent,
  forwardRef,
  useLayoutEffect,
  ReactNode,
} from 'react';

import customScrollStyles from '../CustomScroll/CustomScroll.styles.m.css';
import { useInputClassNames } from '../hooks/useInputClassNames/useInputClassNames';
import styles from './MultiLineField.styles.m.css';

interface IMultiLineFieldProps
  extends DetailedHTMLProps<InputHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement> {
  label?: string | ReactNode;
  hasError?: boolean;
  message?: string;
  active?: boolean;
  isDisabled?: boolean;
  isDynamicHeight?: boolean;
  setRef?: React.Ref<HTMLTextAreaElement>;
  rows?: number;
  cols?: number;
  contentClassName?: string;
}

export const MultiLineField: FC<PropsWithChildren<IMultiLineFieldProps>> = forwardRef(
  (props, ref) => {
    const {
      className,
      label,
      value,
      placeholder,
      hasError,
      message,
      onFocus,
      onBlur,
      onChange,
      active,
      children,
      isDisabled,
      isDynamicHeight = false,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      setRef,
      rows = 1,
      cols = 1,
      contentClassName,
      ...textAreaProps
    } = props;

    const [isFocused, setFocused] = useState(active);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const internalRef = ref || textAreaRef;

    const handleOnFocus = useCallback(
      (event: FocusEvent<HTMLTextAreaElement>) => {
        setFocused(true);
        onFocus?.(event);
      },
      [onFocus]
    );

    const handleOnBlur = useCallback(
      (event: FocusEvent<HTMLTextAreaElement>) => {
        setFocused(false);
        onBlur?.(event);
      },
      [onBlur]
    );

    const autoResize = useCallback(() => {
      if (!isDynamicHeight) return;

      const textarea = typeof internalRef === 'object' ? internalRef.current : null;
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    }, [isDynamicHeight, internalRef]);

    const handleOnChange = useCallback(
      (event: ChangeEvent<HTMLTextAreaElement>) => {
        onChange?.(event);
      },
      [onChange]
    );

    useLayoutEffect(() => {
      autoResize();
    }, [value, autoResize]);

    const {
      containerClassName,
      contentClassName: contentClassNameFromInput,
      inputClassName,
      messageClassName,
      labelClassName,
      inputWrapperClassName,
    } = useInputClassNames({
      isFocused,
      isLabel: !!label,
      isError: hasError,
      isDisabled: isDisabled,
      placeholder,
      value,
    });

    const containerClassNames = classNames(containerClassName, className);
    const contentClassNames = classNames(
      contentClassNameFromInput,
      styles.content,
      contentClassName
    );
    const textareaClassNames = classNames(
      inputClassName,
      styles.textarea,
      customScrollStyles.container
    );

    return (
      <div className={containerClassNames}>
        <div className={contentClassNames}>
          <label
            className={classNames(inputWrapperClassName, styles.inputWrapper, {
              [styles.dynamicHeight]: isDynamicHeight,
            })}
          >
            <span className={labelClassName}>{label}</span>
            <textarea
              {...textAreaProps}
              className={textareaClassNames}
              rows={rows}
              cols={cols}
              ref={internalRef}
              value={value}
              placeholder={placeholder}
              disabled={isDisabled}
              onFocus={handleOnFocus}
              onBlur={handleOnBlur}
              onChange={handleOnChange}
              style={{
                ...textAreaProps.style,
                overflow: isDynamicHeight ? 'hidden' : 'auto',
              }}
            />
          </label>
        </div>
        <div className={messageClassName}>{message || children}</div>
      </div>
    );
  }
);
